import * as Sentry from '@sentry/gatsby';

/**
   * Strips all query parameters from a URL string before it is sent to Sentry,
   * preventing PII/PHI embedded in query params from being logged.
   * Handles both absolute URLs and relative paths (e.g. breadcrumb from/to values).
   *
   * @param rawUrl - The original URL string to sanitize.
   * @returns The URL string with the query string removed.
   */
const clearQueryParams = (rawUrl: string): string => {
    try {
        const url = new URL(rawUrl);
        url.search = '';
        return url.toString();
    } catch {
        // Relative URLs (e.g. breadcrumb from/to paths) are not parseable by URL constructor;
        // fall back to stripping the query string manually while preserving any hash fragment.
        const hashIndex = rawUrl.indexOf('#');
        const baseUrl = hashIndex >= 0 ? rawUrl.slice(0, hashIndex) : rawUrl;
        const fragment = hashIndex >= 0 ? rawUrl.slice(hashIndex) : '';

        return `${baseUrl.split('?')[0]}${fragment}`;
    }
};

if (process.env.GATSBY_EH_ENV === 'prod' || process.env.GATSBY_EH_ENV === 'qa') {
    Sentry.init({
        beforeSend(event: Sentry.Event) {
            if (event.request?.url) {
                event.request.url = clearQueryParams(event.request.url);
            }
            if (event.request?.headers?.Referer) {
                event.request.headers.Referer = clearQueryParams(event.request.headers.Referer);
            }
            if (event.breadcrumbs && event.breadcrumbs.length > 0) {
                const navigationBreadcrumbs = event.breadcrumbs.filter((breadcrumb: Sentry.Breadcrumb) => breadcrumb.category === 'navigation');
                navigationBreadcrumbs.forEach((breadcrumb: Sentry.Breadcrumb) => {
                    if (breadcrumb.data?.from) {
                        breadcrumb.data.from = clearQueryParams(breadcrumb.data.from);
                    }
                    if (breadcrumb.data?.to) {
                        breadcrumb.data.to = clearQueryParams(breadcrumb.data.to);
                    }
                });
            }
            return event;
        },
        dsn: 'https://d5123a0c141510bfa6ac69244570951d@o978409.ingest.us.sentry.io/4506940247965696',
        environment: process.env.GATSBY_EH_ENV,
        integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
        sendDefaultPii: false,
        // Performance Monitoring
        tracesSampleRate: 1.0, //  Capture 100% of the transactions
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        // tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
        // Session Replay
        replaysSessionSampleRate: 1.0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
        ignoreErrors: [/.*Minified React error #.*/, /.*tealium.*/, /.*\[alloy\].*/],
    });
} else {
    if (typeof window !== 'undefined') {
        const SentryMock = {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            addBreadcrumb: (crumb: any) => {
                const level: string = crumb ? crumb.level : null;
                if (/info/i.test(level)) {
                    console.info(crumb);
                } else if (/warn/i.test(level)) {
                    console.warn(crumb);
                } else if (/error/i.test(level)) {
                    console.error(crumb);
                } else {
                    console.log(crumb);
                }
            },
            captureEvent: () => console.log('mocking Sentry captureEvent fn'),
            captureException: () => console.log('mocking Sentry captureException fn'),
            captureMessage: () => console.log('mocking Sentry captureMessage fn'),
            configureScope: () => console.log('mocking Sentry configureScope fn'),
            forceLoad: () => console.log('mocking Sentry forceLoad fn'),
            init: () => console.log('mocking Sentry init fn'),
            onLoad: () => console.log('mocking Sentry onLoad fn'),
            showReportDialog: () => console.log('mocking Sentry showReportDialog fn'),
            withScope: () => console.log('mocking Sentry withScope fn'),
        };

        Object.defineProperty(window, 'Sentry', {
            writable: false,
            value: SentryMock,
        });
    }
}
